import { AddImageIcon } from 'assets/icons';
import { CustomButton, CustomModal, MultiFileUpload } from 'components';
import { ButtonType, FormButtonType } from 'components/custom-button/CustomButton';
import { FC, useCallback, useRef, useState } from 'react';
import { UploadAttachmentFile, VoidFn } from 'types/baseTypes';
import { useGetPresignedUrlsMutation } from 'containers/invoices/api';
import { FILE_TYPE } from 'constants/common';
import FormCloseReactIcon from 'assets/react-icons/FormCloseReactIcon';
import { PresignedUrlAttachment } from 'containers/invoices/invoices.model';
import { useAppDispatch, useAppSelector } from 'hooks';
import { NotifierTypes } from 'constants/notifierConstants';
import { showNotification } from 'utils/Notifier';
import { uploadFileToPresignedUrl } from 'services/presignedUpload';
import { selectCurrentTeam } from 'containers/app/appSlice';

interface UploadAttachmentToPresignedModalProps {
  className?: string;
  open: boolean;
  toggleOpen: VoidFn;
  onSave: (attachments: UploadAttachmentFile[]) => void;
  useCase: string;
  allowImageCompression?: boolean;
  supportedFileTypes?: {
    [key: string]: string[];
  };
  maxFiles?: number;
  idPrefix?: string;
}

const UploadAttachmentToPresignedModal: FC<UploadAttachmentToPresignedModalProps> = ({
  open,
  toggleOpen,
  onSave,
  useCase,
  allowImageCompression = true,
  supportedFileTypes = {
    [FILE_TYPE.JPG]: [],
    [FILE_TYPE.JPEG]: [],
    [FILE_TYPE.PNG]: [],
    [FILE_TYPE.WEBP]: []
  },
  maxFiles,
  idPrefix = 'upload-attachment'
}) => {
  const currentTeam = useAppSelector(selectCurrentTeam);
  const teamId = currentTeam?.id?.toString() || '';
  const dispatch = useAppDispatch();
  const [getPresignedUrls] = useGetPresignedUrlsMutation();
  const [files, setFiles] = useState<UploadAttachmentFile[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const saveAbortRef = useRef<AbortController | null>(null);

  const handleFileRemove = useCallback((fileId: number | string) => {
    setFiles(prev => prev.filter(file => file.id !== fileId && file.tempId !== fileId));
  }, []);

  const handleSave = async () => {
    if (files.length === 0) {
      return;
    }

    setIsSaving(true);
    saveAbortRef.current = new AbortController();
    const signal = saveAbortRef.current.signal;

    try {
      // Use data from files state - file.title for filename, and fileType
      const attachments: PresignedUrlAttachment[] = files.map((file: UploadAttachmentFile) => ({
        filename: file.title,
        fileType: file.fileType
      }));

      const presignedUrlsResponse = await getPresignedUrls({
        teamId,
        case: useCase,
        attachments
      }).unwrap();

      const uploadPromises = files.map(async (file: UploadAttachmentFile, index) => {
        try {
          const presignedUrlItem = presignedUrlsResponse.data[index];
          if (!presignedUrlItem) {
            return null;
          }

          // Get File object from file.rawFile
          if (!file.rawFile) {
            return null;
          }

          const uploadSuccess = await uploadFileToPresignedUrl(file.rawFile, presignedUrlItem.presignedURL, signal);
          if (!uploadSuccess) {
            return null;
          }

          // Update file with path
          const uploadedFile: UploadAttachmentFile = {
            tempId: file.tempId,
            id: file.id,
            title: file.title,
            description: file.description,
            url: presignedUrlItem.path,
            urlLink: presignedUrlItem.presignedURL,
            getUrlLink: presignedUrlItem.getPresignedURL,
            fileType: file.fileType,
            previewData: file.previewData
          };

          return uploadedFile;
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            throw error;
          }
          return null;
        }
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      const successfulUploads: UploadAttachmentFile[] = uploadedFiles.filter(
        (file): file is UploadAttachmentFile => file !== null
      );
      const hasFailedUploads = successfulUploads.length < files.length;

      if (successfulUploads.length > 0) {
        if (hasFailedUploads) {
          showNotification(
            dispatch,
            NotifierTypes.ERROR,
            'Failed to upload some files. Please try again.',
            4000,
            'file-upload-failed'
          );
        }
        onSave(successfulUploads);
        onResetState();
      } else {
        showNotification(
          dispatch,
          NotifierTypes.ERROR,
          'Failed to save files. Please try again.',
          4000,
          'upload-failed'
        );
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      showNotification(dispatch, NotifierTypes.ERROR, 'Failed to save files. Please try again.', 4000, 'upload-failed');
    } finally {
      saveAbortRef.current = null;
      setIsSaving(false);
    }
  };

  const onResetState = () => {
    saveAbortRef.current?.abort();
    setFiles([]);
    setIsUploading(false);
    toggleOpen();
  };

  const isSaveButtonDisabled = () => {
    return files.length === 0 || isUploading || isSaving;
  };

  return (
    <div>
      <CustomModal width="md:w-[610px]" open={open} closeModal={onResetState}>
        <div className="relative w-full py-8">
          <FormCloseReactIcon
            id={`${idPrefix}-close-icon`}
            className="absolute top-3 right-3 shrink-0"
            onClick={onResetState}
          />
          <div className="mb-4 px-10 text-xl font-bold text-heading ">Add photos</div>
          <div
            className="customNormalScroll flex max-h-[485px] w-full flex-col overflow-y-auto 
          border-y border-borderGray px-10 py-2">
            <MultiFileUpload
              id={`${idPrefix}-upload`}
              files={files}
              setFiles={setFiles}
              onFileRemove={handleFileRemove}
              teamId={teamId}
              useCase={useCase}
              className="mb-3"
              height={files?.length > 1 ? 'h-[120px]' : 'h-[250px]'}
              FileUploadIcon={AddImageIcon}
              supportedFileTypes={supportedFileTypes}
              allowImageCompression={allowImageCompression}
              maxFiles={maxFiles}
              isUploading={isUploading}
              setIsUploading={setIsUploading}
            />
          </div>
          <div className="mt-4 flex flex-row justify-end gap-5 px-10">
            <CustomButton id={`${idPrefix}-cancel`} className="w-24" type={ButtonType.SECONDARY} onClick={onResetState}>
              Cancel
            </CustomButton>
            <CustomButton
              id={`${idPrefix}-save`}
              onClick={handleSave}
              formType={FormButtonType.button}
              className="w-24"
              isLoading={isSaving}
              disabled={isSaveButtonDisabled()}>
              Save
            </CustomButton>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default UploadAttachmentToPresignedModal;
